/**
 * Shared rules for which catalog products may appear in upsell offers.
 * Excludes draft/archived/unpublished products and out-of-stock variants
 * (inventory_policy deny + zero quantity).
 */

function normalizeInventoryPolicy(variant) {
  return String(variant?.inventory_policy || variant?.inventoryPolicy || "")
    .trim()
    .toLowerCase();
}

function readInventoryQuantity(variant) {
  const candidates = [
    variant?.sellable_online_quantity,
    variant?.sellableOnlineQuantity,
    variant?.inventory_quantity,
    variant?.inventoryQuantity,
    variant?.quantityAvailable,
  ];

  for (const value of candidates) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
  }

  return null;
}

function isExplicitlyUnavailable(variant) {
  return variant?.available === false || variant?.availableForSale === false;
}

function isExplicitlyAvailable(variant) {
  return variant?.available === true || variant?.availableForSale === true;
}

function isInventoryUntracked(variant) {
  const item = variant?.inventoryItem ?? variant?.inventory_item;
  return item?.tracked === false;
}

/** @param {object | null | undefined} variant */
export function isVariantOfferable(variant) {
  if (!variant || typeof variant !== "object") {
    return false;
  }

  if (isExplicitlyUnavailable(variant)) {
    return false;
  }

  if (isInventoryUntracked(variant)) {
    return true;
  }

  const policy = normalizeInventoryPolicy(variant);
  const quantity = readInventoryQuantity(variant);

  if (isExplicitlyAvailable(variant) && policy === "deny" && quantity !== null && quantity <= 0) {
    return true;
  }

  if (policy === "deny") {
    if (quantity === null) {
      return isExplicitlyAvailable(variant);
    }
    return quantity > 0;
  }

  if (policy === "continue") {
    return true;
  }

  if (isExplicitlyAvailable(variant)) {
    return true;
  }

  if (quantity !== null) {
    return quantity > 0;
  }

  return false;
}

function resolveProductRecord(product) {
  if (!product || typeof product !== "object") {
    return null;
  }
  return product.productInfo && typeof product.productInfo === "object"
    ? product.productInfo
    : product;
}

function isActivePublishedProduct(record) {
  const status = String(record?.status || "").trim().toLowerCase();
  if (status && status !== "active") {
    return false;
  }

  if (record?.available === false) {
    return false;
  }

  const publishedAt = record?.published_at ?? record?.publishedAt;
  if (publishedAt === null || publishedAt === "") {
    return false;
  }

  return true;
}

/** @param {object | null | undefined} product */
export function isProductOfferable(product) {
  const record = resolveProductRecord(product);
  if (!record) {
    return false;
  }

  if (!isActivePublishedProduct(record)) {
    return false;
  }

  const variants = Array.isArray(record.variants) ? record.variants : [];
  if (!variants.length) {
    return false;
  }

  return variants.some(isVariantOfferable);
}

/**
 * Return product with only in-stock / sellable variants, or null if none remain.
 * Preserves wrapper shape when input uses `productInfo`.
 * @param {object | null | undefined} product
 * @returns {object | null}
 */
export function toOfferableProduct(product) {
  const record = resolveProductRecord(product);
  if (!record || !isActivePublishedProduct(record)) {
    return null;
  }

  const variants = Array.isArray(record.variants) ? record.variants : [];
  const offerableVariants = variants.filter(isVariantOfferable);
  if (!offerableVariants.length) {
    return null;
  }

  if (product?.productInfo && typeof product.productInfo === "object") {
    return {
      ...product,
      productInfo: { ...record, variants: offerableVariants },
    };
  }

  return { ...record, variants: offerableVariants };
}

/**
 * @param {object[]} products
 * @param {{ limit?: number }} [options]
 * @returns {object[]}
 */
export function filterOfferableProducts(products, options = {}) {
  const pool = Array.isArray(products) ? products : [];
  const filtered = pool.map(toOfferableProduct).filter(Boolean);

  if (options.limit == null) {
    return filtered;
  }

  const safeLimit = Math.max(0, Number(options.limit) || 0);
  return filtered.slice(0, safeLimit);
}

/**
 * Keep active/published products even when OOS (badge/disable modes).
 * Attaches `weupsellOutOfStock` when no sellable variants remain.
 * @param {object | null | undefined} product
 * @returns {object | null}
 */
export function toStockAwareProduct(product) {
  const record = resolveProductRecord(product);
  if (!record || !isActivePublishedProduct(record)) {
    return null;
  }

  const variants = Array.isArray(record.variants) ? record.variants : [];
  const offerable = variants.some(isVariantOfferable);
  const tagged = { weupsellOutOfStock: !offerable };

  if (product?.productInfo && typeof product.productInfo === "object") {
    return {
      ...product,
      ...tagged,
      productInfo: { ...record },
    };
  }

  return { ...record, ...tagged };
}

/**
 * Filter offer products by stocks OOS behavior (hide | badge | disable).
 * @param {object[]} products
 * @param {'hide'|'badge'|'disable'|string} [oosBehavior]
 * @param {{ limit?: number }} [options]
 */
export function filterProductsByOosBehavior(
  products,
  oosBehavior = "hide",
  options = {}
) {
  const mode = String(oosBehavior || "hide").trim().toLowerCase();
  if (mode !== "badge" && mode !== "disable") {
    return filterOfferableProducts(products, options);
  }

  const pool = Array.isArray(products) ? products : [];
  const filtered = pool.map(toStockAwareProduct).filter(Boolean);

  if (options.limit == null) {
    return filtered;
  }

  const safeLimit = Math.max(0, Number(options.limit) || 0);
  return filtered.slice(0, safeLimit);
}

/** Fetch multiplier so filtering still returns enough in-stock candidates. */
export function offerabilityFetchMultiplier(requestedCount = 1) {
  const base = Math.max(1, Number(requestedCount) || 1);
  return Math.min(50, Math.max(base * 5, 10));
}

/** Normalize Shopify product id / GID to numeric string for comparisons. */
export function normalizeShopifyProductId(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw.includes("/") ? raw.split("/").pop() : raw;
}

/** Source + cart/order/context ids that must not appear as recommendation offers. */
export function buildExcludeProductIdSet(sourceProductId, excludeProductIds = []) {
  const set = new Set();
  const add = (value) => {
    const id = normalizeShopifyProductId(value);
    if (id) set.add(id);
  };

  add(sourceProductId);
  const extras = Array.isArray(excludeProductIds)
    ? excludeProductIds
    : String(excludeProductIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

  for (const id of extras) {
    add(id);
  }

  return set;
}

export function isExcludedProductId(productId, excludeSet) {
  if (!excludeSet?.size) return false;
  return excludeSet.has(normalizeShopifyProductId(productId));
}

function normalizeProductIdList(productIds) {
  const raw = Array.isArray(productIds)
    ? productIds
    : String(productIds || "")
        .split(",")
        .map((id) => id.trim())
        .filter(Boolean);

  const seen = new Set();
  const ordered = [];
  for (const value of raw) {
    const id = normalizeShopifyProductId(value);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ordered.push(id);
  }
  return ordered;
}

/**
 * Reorder products to match the caller's product id sequence.
 * @param {object[]} products
 * @param {string[] | string} productIds
 * @returns {object[]}
 */
export function orderOfferProductsByIds(products, productIds) {
  const requestedOrder = normalizeProductIdList(productIds);
  if (!requestedOrder.length) {
    return Array.isArray(products) ? products : [];
  }

  const byId = new Map();
  for (const product of Array.isArray(products) ? products : []) {
    const record = resolveProductRecord(product);
    const id = normalizeShopifyProductId(record?.id);
    if (!id || byId.has(id)) continue;
    byId.set(id, product);
  }

  return requestedOrder.map((id) => byId.get(id)).filter(Boolean);
}

/**
 * Pick offerable products in the caller's product id order (no shuffle).
 * @param {object[]} products
 * @param {{ limit?: number, excludeProductIds?: string[] | string, productIds?: string[] | string }} [options]
 */
export function pickOfferProductsInOrder(products, options = {}) {
  const excludeSet = buildExcludeProductIdSet(null, options.excludeProductIds);
  const requestedOrder = normalizeProductIdList(options.productIds);
  const pool = orderOfferProductsByIds(products, requestedOrder);

  const ordered = [];
  const seen = new Set();
  for (const product of pool) {
    const record = resolveProductRecord(product);
    const id = normalizeShopifyProductId(record?.id);
    if (!id || seen.has(id)) continue;
    if (!isProductOfferable(record)) continue;
    if (isExcludedProductId(record?.id, excludeSet)) continue;
    const offerable = toOfferableProduct(product);
    if (!offerable) continue;
    ordered.push(offerable);
    seen.add(id);
  }

  const requestedCount = Math.max(1, Number(options.limit) || 1);
  return ordered.slice(0, Math.min(requestedCount, ordered.length));
}

/**
 * Shuffle and pick offerable products, excluding context product ids.
 * @param {object[]} products
 * @param {{ limit?: number, excludeProductIds?: string[] | string }} [options]
 */
export function pickRandomOfferProducts(products, options = {}) {
  const excludeSet = buildExcludeProductIdSet(null, options.excludeProductIds);
  const pool = (Array.isArray(products) ? products : []).filter((product) => {
    const record = resolveProductRecord(product);
    if (!isProductOfferable(record)) return false;
    return !isExcludedProductId(record?.id, excludeSet);
  });

  const requestedCount = Math.max(1, Number(options.limit) || 1);
  const shuffled = [...pool].sort(() => 0.5 - Math.random());
  return shuffled.slice(0, Math.min(requestedCount, shuffled.length));
}
